import React from 'react';

/**
 * Ordered quantity split into received · cancelled · open, for PO lines that had a cancellation (2026-09-14).
 * Green received, red hatch cancelled, amber hatch while a cancellation waits for approval, empty = still expected.
 */
export interface QtySplitCellProps {
  ordered: number;
  received: number;
  cancelled: number;
  /** Quantity of a cancellation waiting for approval (part of the open quantity). */
  pending?: number;
  suffix?: React.ReactNode;
  compact?: boolean;
}

const fmt = (n: number) => Number(n).toLocaleString('en-US', { maximumFractionDigits: 2 });
const hatch = (c: string) => `repeating-linear-gradient(135deg, ${c} 0 2px, transparent 2px 5px)`;

const QtySplitCell: React.FC<QtySplitCellProps> = ({ ordered, received, cancelled, pending = 0, suffix, compact }) => {
  const pct = (n: number) => (ordered > 0 ? Math.max(0, Math.min(100, (n / ordered) * 100)) : 0);
  const open = Math.max(0, ordered - received - cancelled);
  const parts = [
    `${fmt(received)} received`,
    cancelled > 0 ? `${fmt(cancelled)} cancelled` : null,
    pending > 0 ? `${fmt(pending)} to cancel` : open > 0 ? `${fmt(open)} open` : null,
  ].filter(Boolean);
  return (
    <span style={{ display: 'inline-block', whiteSpace: 'nowrap', fontVariantNumeric: 'tabular-nums' }}>
      {!compact && (
        <span
          style={{
            display: 'flex',
            width: 96,
            height: 8,
            marginBottom: 3,
            background: '#f5f6f8',
            border: '1px solid #cfd4dd',
            borderRadius: 4,
            overflow: 'hidden',
          }}
        >
          <span style={{ width: `${pct(received)}%`, background: '#1a7f4b' }} />
          <span style={{ width: `${pct(cancelled)}%`, background: hatch('#b42323') }} />
          <span style={{ width: `${pct(pending)}%`, background: hatch('#d48806') }} />
        </span>
      )}
      <span style={{ fontSize: 12, color: '#4b5563' }}>
        {parts.join(' · ')}
        {suffix ? <span style={{ color: '#8a94a6', marginLeft: 4 }}>{suffix}</span> : null}
      </span>
    </span>
  );
};

export default QtySplitCell;
